import { type CountryCode, getCountries, getCountryCallingCode } from 'libphonenumber-js/max';

export type PhoneCountry = { code: CountryCode; label: string; callingCode: string };

export const DEFAULT_PHONE_COUNTRY: CountryCode = 'FR';

/** Regional-indicator pair for an ISO 3166-1 alpha-2 code — renders as the country's flag. */
export const countryFlag = (code: string): string =>
  String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1f1e6 + c.charCodeAt(0) - 65));

const cache = new Map<string, PhoneCountry[]>();

/**
 * The dialable countries, labelled for the active locale and sorted by that label.
 * `getCountryOptions` is keyed by display name rather than ISO code, so names are resolved here.
 */
export const getPhoneCountries = (locale: string | undefined): PhoneCountry[] => {
  // i18next reports no language until it is initialised, and Intl.DisplayNames throws on undefined.
  const resolved = locale || 'fr';
  const cached = cache.get(resolved);
  if (cached) return cached;

  const display = new Intl.DisplayNames([resolved], { type: 'region' });
  const countries = getCountries()
    .map(code => ({
      code,
      label: display.of(code) ?? code,
      callingCode: getCountryCallingCode(code),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, resolved));

  cache.set(resolved, countries);
  return countries;
};
